/**
 * Lab high-stock rate load: GET `runner.u`, on fail GET `runner.fu`.
 * S3 NoSuchKey (or any non-OK) surfaces as error text for the chart panel.
 */

import { resolveRateUrl } from './rateBuckets'

/** Lab S3 rate tick: unix sec + optional back/lay. */
export type LabRateTick = {
  d: number
  b?: number
  l?: number
  bs?: number
  ls?: number
}

export type FetchRunnerRatesResult =
  | {
      ok: true
      rates: [number, number][]
      /** Raw S3 ticks — Highstock Back/Lay + volume tooltips. */
      ticks: LabRateTick[]
      source: 'u' | 'fu'
    }
  | { ok: false; error: string }

function isStaticUrl(url: string): boolean {
  return url.startsWith('static://') || !url.trim()
}

/** Map Lab rate ticks → chart `[unixSec, backPrice]` (carry-forward like highStock). */
export function labRateTicksToSeries(ticks: LabRateTick[]): [number, number][] {
  let oldBack = 0
  const out: [number, number][] = []
  for (const m of ticks) {
    if (!Number.isFinite(m.d)) continue
    if (typeof m.b === 'number' && Number.isFinite(m.b)) oldBack = m.b
    if (!oldBack) continue
    out.push([m.d, oldBack])
  }
  return out
}

/**
 * Bookmaker market ids embed `#` (e.g. `1.262929753#bookmaker1`).
 * In a URL that starts a fragment — browsers/fetch drop `#…` and S3 sees a
 * truncated key (404 on `…/1.262929753` instead of `…/1.262929753#bookmaker1/1.json`).
 */
export function encodeRateFetchUrl(url: string): string {
  return url.replace(/#/g, '%23')
}

async function getJsonRates(url: string): Promise<LabRateTick[]> {
  const res = await fetch(encodeRateFetchUrl(url), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  const text = await res.text()
  if (!res.ok) {
    throw new Error(text.trim() || `HTTP ${res.status}`)
  }
  const data = JSON.parse(text) as unknown
  if (!Array.isArray(data)) {
    throw new Error('Rate JSON is not an array')
  }
  return data as LabRateTick[]
}

export async function fetchRunnerRates(
  primaryUrl: string,
  fallbackUrl: string,
): Promise<FetchRunnerRatesResult> {
  const u = resolveRateUrl(primaryUrl, 'primary')
  const fu = resolveRateUrl(fallbackUrl, 'fallback')

  if (isStaticUrl(u) && isStaticUrl(fu)) {
    return { ok: false, error: 'No rate URL (static demo has embedded rates)' }
  }

  if (!isStaticUrl(u)) {
    try {
      const ticks = await getJsonRates(u)
      return {
        ok: true,
        rates: labRateTicksToSeries(ticks),
        ticks,
        source: 'u',
      }
    } catch (err) {
      // fall through to fu
      if (isStaticUrl(fu)) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        }
      }
    }
  }

  try {
    const ticks = await getJsonRates(fu)
    return {
      ok: true,
      rates: labRateTicksToSeries(ticks),
      ticks,
      source: 'fu',
    }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    }
  }
}
